/**
 * Form field primitives: Input, Textarea, Select, FieldRow, FieldError.
 *
 * Pure presentational — pair with react-hook-form using `register(...)`
 * spread on the underlying element.
 */
import { forwardRef } from 'react';
import { classes } from '@/lib/utils';

const baseInput =
  'w-full bg-black/40 border border-white/10 rounded-2xl px-4 py-2.5 text-sm text-white ' +
  'placeholder:text-white/30 focus:outline-none focus:border-gold/50 focus:shadow-glow-gold-sm ' +
  'transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

export const Input = forwardRef(function Input(
  { className, error, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={classes(baseInput, error && 'border-red-500/50', className)}
      {...rest}
    />
  );
});

export const Textarea = forwardRef(function Textarea(
  { className, rows = 3, error, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={classes(baseInput, 'resize-none', error && 'border-red-500/50', className)}
      {...rest}
    />
  );
});

export const Select = forwardRef(function Select(
  { className, children, error, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      className={classes(baseInput, 'appearance-none pr-10 cursor-pointer', error && 'border-red-500/50', className)}
      {...rest}
    >
      {children}
    </select>
  );
});

export function FieldRow({ label, hint, error, required, children, className }) {
  return (
    <label className={classes('block', className)}>
      {label ? (
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs uppercase tracking-wider text-gold/80 font-semibold">
            {label}
            {required ? <span className="text-gold ml-1">*</span> : null}
          </span>
          {hint ? <span className="text-[11px] text-white/40">{hint}</span> : null}
        </div>
      ) : null}
      {children}
      {error ? (
        <div className="mt-1.5 text-xs text-red-400" data-testid="field-error">
          {error}
        </div>
      ) : null}
    </label>
  );
}

/** Color picker hex input + swatch preview. */
export const ColorField = forwardRef(function ColorField(
  { value, onChange, ...rest },
  ref,
) {
  return (
    <div className="flex items-center gap-3">
      <div
        className="w-10 h-10 rounded-xl border border-white/10 shrink-0"
        style={{ background: value || '#C9A84C' }}
      />
      <input
        ref={ref}
        type="text"
        value={value || ''}
        onChange={onChange}
        placeholder="#C9A84C"
        className={baseInput}
        {...rest}
      />
    </div>
  );
});
